import { ActionIcon, Group, Paper, Tooltip } from '@mantine/core';
import { IconArrowRight, IconFilter, IconFilterOff, IconPlus, IconWashDrycleanOff, IconX } from '@tabler/icons-react';
import { useContextMenu } from 'mantine-contextmenu';
import * as React from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { when } from '../../../../../../react-commons/utils/when';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { getEnumFormatter } from '../../../../resource/enumerations/utils/getEnumFormatter';
import { VCycleElementContainerFilterConditionSelector } from '../../../../resource/enumerations/VCycleElementContainerFilterConditionSelector';
import { VCycleElementContainerFilterLogicalConnector } from '../../../../resource/enumerations/VCycleElementContainerFilterLogicalConnector';
import { defaultParentedPersistableResource } from '../../../../resource/resource-base/persistableResourceTypes';
import { VCycleElementContainerFilterResource } from '../../../../resource/resources/persistables/user/vcycleElementContainerFilterResource';
import { ControlledReferenceInputs } from '../../../reusable-components/base/reference-text-fields/ControlledReferenceInputs';

export const FilterBarComponent = ({
    onApplyFilter,
}: {
    onApplyFilter: (value: VCycleElementContainerFilterResource | null) => void;
}) => {
    const { versionId } = useParams<{ versionId: string }>();
    const [currentFilter, setCurrentFilter] = useState<VCycleElementContainerFilterResource | null>(null);
    const [showAddButton, setShowAddButton] = useState<boolean>(true);
    const [filterApplied, setFilterApplied] = useState<boolean>(false);
    const { showContextMenu } = useContextMenu();

    const getReferenceInput = (
        conditionSelector: VCycleElementContainerFilterConditionSelector,
        objectIds: string[] | undefined,
        onChange: (newValue: string[] | null) => void
    ) => {
        return when({
            expression: conditionSelector,
            cases: {
                [VCycleElementContainerFilterConditionSelector.PROCESS_AREA_ROLE]: (
                    <ControlledReferenceInputs.Role
                        objectIds={objectIds}
                        projectVersionId={versionId}
                        readOnly={false}
                        onChange={onChange}
                        noLabel={true}
                    />
                ),
                [VCycleElementContainerFilterConditionSelector.RELEVANT_ROLE]: (
                    <ControlledReferenceInputs.Role
                        objectIds={objectIds}
                        projectVersionId={versionId}
                        readOnly={false}
                        onChange={onChange}
                        noLabel={true}
                    />
                ),
                [VCycleElementContainerFilterConditionSelector.CRITERIA]: (
                    <ControlledReferenceInputs.Criteria
                        objectIds={objectIds}
                        projectVersionId={versionId}
                        readOnly={false}
                        onChange={onChange}
                        noLabel={true}
                    />
                ),
                [VCycleElementContainerFilterConditionSelector.METHOD]: (
                    <ControlledReferenceInputs.Method
                        objectIds={objectIds}
                        projectVersionId={versionId}
                        readOnly={false}
                        onChange={onChange}
                        noLabel={true}
                    />
                ),
                [VCycleElementContainerFilterConditionSelector.TOOL]: (
                    <ControlledReferenceInputs.Tool
                        objectIds={objectIds}
                        projectVersionId={versionId}
                        readOnly={false}
                        onChange={onChange}
                        noLabel={true}
                    />
                ),
                [VCycleElementContainerFilterConditionSelector.OBJECTIVE]: (
                    <ControlledReferenceInputs.Objective
                        objectIds={objectIds}
                        projectVersionId={versionId}
                        readOnly={false}
                        onChange={onChange}
                        noLabel={true}
                    />
                ),
                [VCycleElementContainerFilterConditionSelector.PROCESS_ARTIFACT]: (
                    <ControlledReferenceInputs.ProcessArtifact
                        objectIds={objectIds}
                        projectVersionId={versionId}
                        readOnly={false}
                        onChange={onChange}
                        noLabel={true}
                    />
                ),
                [VCycleElementContainerFilterConditionSelector.VERIFICATION_METRIC]: (
                    <ControlledReferenceInputs.VerificationMetric
                        objectIds={objectIds}
                        projectVersionId={versionId}
                        readOnly={false}
                        onChange={onChange}
                        noLabel={true}
                    />
                ),
            },
        });
    };

    return (
        <Paper
            withBorder
            p={'xs'}
            shadow={'xs'}
            style={{ width: window.innerWidth - 120, minHeight: 58, alignContent: 'center' }}
        >
            <Group w={'100%'} wrap={'nowrap'}>
                <ActionIcon
                    onClick={(e) => {
                        if (filterApplied) {
                            setFilterApplied(false);
                            // setCurrentFilter(null)
                            onApplyFilter(null);
                        } else {
                            showContextMenu([
                                {
                                    key: 'new_filter',
                                    title: 'Add Filter',
                                    icon: <IconPlus />,
                                    onClick: () => {
                                        setShowAddButton(true);
                                        setCurrentFilter({
                                            ...defaultParentedPersistableResource,
                                            resourceType: ResourceType.V_CYCLE_ELEMENT_CONTAINER_FILTER,
                                            startingCondition: {
                                                selector: VCycleElementContainerFilterConditionSelector.RELEVANT_ROLE,
                                                values: [],
                                            },
                                            otherConditions: [],
                                        });
                                    },
                                },
                                {
                                    key: 'clear_filter',
                                    title: 'Clear Filter',
                                    icon: <IconWashDrycleanOff />,
                                    hidden: currentFilter === null,
                                    onClick: () => {
                                        setShowAddButton(true);
                                        setCurrentFilter(null);
                                    },
                                },
                            ])(e);
                        }
                    }}
                    variant={'transparent'}
                    color={'var(--mantine-color-base-button)'}
                >
                    {filterApplied ?
                        <IconFilterOff />
                    :   <IconFilter />}
                </ActionIcon>
                <Group>
                    {currentFilter && (
                        <>
                            <div style={{ minWidth: 150, maxWidth: 150 }}>
                                <ControlledInputs.Enum
                                    nullable={false}
                                    readOnly={false}
                                    onChange={(value) => {
                                        const newFilter = { ...currentFilter!! };
                                        newFilter.startingCondition.selector = value;
                                        newFilter.startingCondition.values = [];
                                        setCurrentFilter(newFilter);
                                    }}
                                    value={currentFilter!!.startingCondition.selector}
                                    enumType={VCycleElementContainerFilterConditionSelector}
                                    enumFormatter={getEnumFormatter(VCycleElementContainerFilterConditionSelector)}
                                />
                            </div>
                            <div style={{ minWidth: 250, maxWidth: 250 }}>
                                {getReferenceInput(
                                    currentFilter!!.startingCondition.selector,
                                    currentFilter!!.startingCondition.values,
                                    (newValue) => {
                                        const newFilter = { ...currentFilter!! };
                                        newFilter.startingCondition.values = newValue ?? [];
                                        setCurrentFilter(newFilter);
                                        if (newValue !== null && !newFilter.otherConditions.length) {
                                            setShowAddButton(true);
                                        }
                                    }
                                )}
                            </div>
                        </>
                    )}
                    {currentFilter && (
                        <>
                            {currentFilter!!.otherConditions.map((value, index) => (
                                <Group key={index}>
                                    <div style={{ minWidth: 100, maxWidth: 100 }}>
                                        <ControlledInputs.Enum
                                            nullable={false}
                                            readOnly={false}
                                            onChange={(value) => {
                                                const newFilter = { ...currentFilter!! };
                                                newFilter.otherConditions[index].logicalConnector = value;
                                                setCurrentFilter(newFilter);
                                            }}
                                            value={value.logicalConnector}
                                            enumType={VCycleElementContainerFilterLogicalConnector}
                                            enumFormatter={getEnumFormatter(
                                                VCycleElementContainerFilterLogicalConnector
                                            )}
                                        />
                                    </div>
                                    <div style={{ minWidth: 150, maxWidth: 150 }}>
                                        <ControlledInputs.Enum
                                            nullable={false}
                                            readOnly={false}
                                            onChange={(value) => {
                                                const newFilter = { ...currentFilter!! };
                                                newFilter.otherConditions[index].selector = value;
                                                newFilter.otherConditions[index].values = [];
                                                setCurrentFilter(newFilter);
                                            }}
                                            value={value.selector}
                                            enumType={VCycleElementContainerFilterConditionSelector}
                                            enumFormatter={getEnumFormatter(
                                                VCycleElementContainerFilterConditionSelector
                                            )}
                                        />
                                    </div>
                                    <div style={{ minWidth: 250, maxWidth: 250 }}>
                                        {getReferenceInput(value.selector, value.values, (newValue) => {
                                            const newFilter = { ...currentFilter!! };
                                            newFilter.otherConditions[index].values = newValue ?? [];
                                            setCurrentFilter(newFilter);
                                        })}
                                    </div>
                                </Group>
                            ))}
                            <Show
                                when={!showAddButton}
                                fallback={
                                    <Group gap={2}>
                                        <Show when={currentFilter.otherConditions.length}>
                                            <ActionIcon
                                                onClick={() => {
                                                    const newFilter = { ...currentFilter!! };
                                                    newFilter.otherConditions = newFilter.otherConditions.filter(
                                                        (value) =>
                                                            value !==
                                                            newFilter.otherConditions[
                                                                newFilter.otherConditions.length - 1
                                                            ]
                                                    );
                                                    setCurrentFilter(newFilter);
                                                }}
                                                variant={'transparent'}
                                                color={'var(--mantine-color-base-button)'}
                                            >
                                                <IconX />
                                            </ActionIcon>
                                        </Show>
                                        <ActionIcon
                                            onClick={() => setShowAddButton(false)}
                                            variant={'transparent'}
                                            color={'var(--mantine-color-base-button)'}
                                        >
                                            <IconPlus />
                                        </ActionIcon>
                                    </Group>
                                }
                            >
                                <div style={{ minWidth: 100, maxWidth: 100 }}>
                                    <ControlledInputs.Enum
                                        nullable={false}
                                        readOnly={false}
                                        onChange={(value) => {
                                            const newFilter = { ...currentFilter!! };
                                            newFilter.otherConditions[newFilter.otherConditions.length] = {
                                                logicalConnector: value,
                                                selector: VCycleElementContainerFilterConditionSelector.RELEVANT_ROLE,
                                                values: [],
                                            };
                                            setShowAddButton(true);
                                            setCurrentFilter(newFilter);
                                        }}
                                        value={VCycleElementContainerFilterLogicalConnector.AND}
                                        enumType={VCycleElementContainerFilterLogicalConnector}
                                        enumFormatter={getEnumFormatter(VCycleElementContainerFilterLogicalConnector)}
                                    />
                                </div>
                                <div style={{ minWidth: 150, maxWidth: 150 }}>
                                    <ControlledInputs.Enum
                                        nullable={false}
                                        readOnly={false}
                                        onChange={(value) => {
                                            const newFilter = { ...currentFilter!! };
                                            newFilter.otherConditions[newFilter.otherConditions.length] = {
                                                logicalConnector: VCycleElementContainerFilterLogicalConnector.AND,
                                                selector: value,
                                                values: [],
                                            };
                                            setShowAddButton(true);
                                            setCurrentFilter(newFilter);
                                        }}
                                        value={VCycleElementContainerFilterConditionSelector.RELEVANT_ROLE}
                                        enumType={VCycleElementContainerFilterConditionSelector}
                                        enumFormatter={getEnumFormatter(VCycleElementContainerFilterConditionSelector)}
                                    />
                                </div>
                                <div style={{ minWidth: 250, maxWidth: 250 }}>
                                    {getReferenceInput(
                                        VCycleElementContainerFilterConditionSelector.RELEVANT_ROLE,
                                        [],
                                        (newValue) => {
                                            if (newValue) {
                                                const newFilter = { ...currentFilter!! };
                                                newFilter.otherConditions[newFilter.otherConditions.length] = {
                                                    logicalConnector: VCycleElementContainerFilterLogicalConnector.AND,
                                                    selector:
                                                        VCycleElementContainerFilterConditionSelector.RELEVANT_ROLE,
                                                    values: newValue,
                                                };
                                                setShowAddButton(true);
                                                setCurrentFilter(newFilter);
                                            }
                                        }
                                    )}
                                </div>
                                <ActionIcon
                                    onClick={() => setShowAddButton(true)}
                                    variant={'transparent'}
                                    color={'var(--mantine-color-base-button)'}
                                >
                                    <IconX />
                                </ActionIcon>
                            </Show>
                        </>
                    )}
                </Group>
                <div style={{ flexGrow: 1 }}></div>
                <Tooltip label={'Apply Filter'}>
                    <ActionIcon
                        disabled={!currentFilter}
                        onClick={() => {
                            setFilterApplied(true);
                            onApplyFilter(currentFilter);
                        }}
                        variant={'transparent'}
                        color={'var(--mantine-color-base-button)'}
                    >
                        <IconArrowRight />
                    </ActionIcon>
                </Tooltip>
            </Group>
        </Paper>
    );
};
